import React from "react";
import { Dialog } from "../../../../ui/components/Dialog";
import { Button } from "../../../../ui/components/Button";
import { FeatherRefreshCw, FeatherAlertTriangle } from "@subframe/core";

export interface CustomerRerunConfirmModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when the user confirms the re-run. */
  onConfirm: () => void;
  /** Customer currently on the invoice (shown as the "from"). Empty = first-time assignment. */
  oldCustomerName?: string | null;
  /** Newly selected customer (shown as the "to"). */
  newCustomerName?: string | null;
  /** True while the re-run is in flight — switches the modal to progress view. */
  loading?: boolean;
  /** Set when the re-run fails — switches the modal to the error view. */
  error?: string | null;
}

/**
 * Confirmation + progress shown when an accountant sets/changes the customer on an invoice.
 *
 * Setting the customer runs the post-match pipeline (GL + tax). Two framings:
 *   - first-time  (no prior customer / unmatched invoice) → "classify" wording, no
 *     overwrite warning (there's nothing to overwrite yet).
 *   - change      (customer already on the invoice)       → "re-run" wording + warning
 *     that current classifications and manual edits will be overwritten.
 *
 * Mirror of the bills VendorRerunConfirmModal, minus TDS (TDS is buyer-side and
 * does not apply to sales invoices).
 *
 * Three visual states driven by `loading` / `error`:
 *   1. confirm  — framing-specific message + Cancel / action button
 *   2. progress — spinner (no dismiss; the page reloads on success, closing this)
 *   3. error    — failure message + Close
 */
export const CustomerRerunConfirmModal: React.FC<
  CustomerRerunConfirmModalProps
> = ({
  open,
  onOpenChange,
  onConfirm,
  oldCustomerName,
  newCustomerName,
  loading = false,
  error = null,
}) => {
  const isFirstTime = !oldCustomerName;

  const confirmTitle = isFirstTime
    ? "Classify this invoice for the selected customer?"
    : "Re-run classifications for this invoice?";
  const confirmButtonLabel = isFirstTime ? "Classify invoice" : "Re-run pipeline";
  const progressTitle = isFirstTime
    ? "Classifying this invoice…"
    : "Re-processing customer…";
  const headerCircle = isFirstTime ? "bg-brand-100" : "bg-warning-100";
  const headerIcon = isFirstTime ? "text-brand-600" : "text-warning-600";
  const confirmBox = isFirstTime
    ? "border-neutral-200 bg-neutral-50"
    : "border-warning-200 bg-warning-50";

  const customerSummary = (oldCustomerName || newCustomerName) && (
    <div className="flex w-full items-center gap-2 rounded-md border border-solid border-neutral-200 bg-neutral-50 px-4 py-3">
      {isFirstTime ? (
        <>
          <span className="text-body font-body text-neutral-400">Customer:</span>
          <span className="text-body font-body-bold text-brand-700 line-clamp-1">
            {newCustomerName || "—"}
          </span>
        </>
      ) : (
        <>
          <span className="text-body font-body-bold text-neutral-700 line-clamp-1">
            {oldCustomerName || "—"}
          </span>
          <span className="text-body font-body text-neutral-400">→</span>
          <span className="text-body font-body-bold text-brand-700 line-clamp-1">
            {newCustomerName || "—"}
          </span>
        </>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className="min-w-[440px] max-w-[540px]">
        <div className="flex w-full flex-col items-start gap-6 px-6 py-6">
          {error ? (
            // ---- 3. Error ---------------------------------------------------
            <>
              <div className="flex w-full items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-error-100">
                  <FeatherAlertTriangle className="h-5 w-5 text-error-600" />
                </div>
                <span className="text-heading-2 font-heading-2 text-default-font">
                  Re-run failed
                </span>
              </div>
              <div className="flex w-full flex-col items-start gap-2 rounded-md border border-solid border-error-200 bg-error-50 px-4 py-4">
                <span className="text-body font-body text-neutral-700 leading-relaxed">
                  {error}
                </span>
              </div>
              <div className="flex w-full items-center justify-end gap-3 pt-1">
                <Button
                  variant="neutral-secondary"
                  onClick={() => onOpenChange(false)}
                >
                  Close
                </Button>
              </div>
            </>
          ) : loading ? (
            // ---- 2. Progress ------------------------------------------------
            <>
              <div className="flex w-full items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-100">
                  <FeatherRefreshCw className="h-5 w-5 text-brand-600 animate-spin" />
                </div>
                <span className="text-heading-2 font-heading-2 text-default-font">
                  {progressTitle}
                </span>
              </div>
              {customerSummary}
              <div className="flex w-full flex-col items-start gap-2 rounded-md border border-solid border-neutral-200 bg-neutral-50 px-4 py-4">
                <span className="text-body font-body text-neutral-700 leading-relaxed">
                  Running{" "}
                  <span className="font-body-bold">
                    GL and tax (IGST / CGST+SGST)
                  </span>{" "}
                  for the selected customer. This usually takes a few seconds —
                  the page refreshes automatically when it's done.
                </span>
              </div>
            </>
          ) : (
            // ---- 1. Confirm -------------------------------------------------
            <>
              <div className="flex w-full items-center gap-3">
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${headerCircle}`}
                >
                  <FeatherRefreshCw className={`h-5 w-5 ${headerIcon}`} />
                </div>
                <span className="text-heading-2 font-heading-2 text-default-font">
                  {confirmTitle}
                </span>
              </div>
              {customerSummary}
              <div
                className={`flex w-full flex-col items-start gap-2 rounded-md border border-solid px-4 py-4 ${confirmBox}`}
              >
                <span className="text-body font-body text-neutral-700 leading-relaxed">
                  {isFirstTime ? (
                    <>
                      This runs{" "}
                      <span className="font-body-bold">
                        GL classification and tax (IGST / CGST+SGST)
                      </span>{" "}
                      for the selected customer for the first time. The extracted
                      invoice data is not changed.
                    </>
                  ) : (
                    <>
                      This re-runs{" "}
                      <span className="font-body-bold">
                        GL classification and tax (IGST / CGST+SGST)
                      </span>{" "}
                      for the new customer and overwrites the invoice's current
                      classifications,{" "}
                      <span className="font-body-bold">
                        including any manual edits you've made
                      </span>
                      . The extracted invoice data is not changed.
                    </>
                  )}
                </span>
              </div>
              <div className="flex w-full items-center justify-end gap-3 pt-1">
                <Button
                  variant="neutral-secondary"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="brand-primary"
                  icon={<FeatherRefreshCw />}
                  onClick={onConfirm}
                >
                  {confirmButtonLabel}
                </Button>
              </div>
            </>
          )}
        </div>
      </Dialog.Content>
    </Dialog>
  );
};

export default CustomerRerunConfirmModal;
